import { MARK_COLORS, MARK_VIEW_EXTENT, diamondPath, ringsForSize } from './primeMarkGeometry.js';
import { PRIME_MARK_ACCESSIBLE_NAME } from '../../../app/PrimeBrandMark.jsx';

/**
 * The PRIME nested-diamond mark, as vector art.
 *
 * This is the *resting* mark. The summon hands off to it — the WebGL canvas
 * fades out while this fades in over the same box — which is why the two are
 * built from the same numbers (`primeMarkGeometry.js`).
 *
 * Drawing the settled mark as SVG rather than parking a WebGL frame is what
 * makes it crisp at any device pixel ratio, free of the post stack's grain and
 * tone mapping, correct after a scroll, and free of GPU cost once it lands.
 *
 * The stroke is expressed in the geometry's own units and scaled by `viewBox`,
 * so the mark stays proportionally correct at every size rather than growing a
 * heavy outline when small.
 */

// The outer stroke straddles r = 1, so the box is opened up by half a stroke on
// each side — without it the diamond's four points are shaved flat. The extent
// is shared with the scene's resting framing so the summon lands at exactly
// this size and the hand-off to this SVG is invisible.
const E = MARK_VIEW_EXTENT;
const VIEW = `${-E} ${-E} ${E * 2} ${E * 2}`;

export function PrimeDiamondMark({
  /**
   * Rendered size in CSS pixels. Always a number, never a CSS length: it also
   * decides whether the sub-40px reduction applies, so it has to be comparable.
   * With `fluid`, it is the *expected* size and the box comes from CSS.
   */
  size = 104,
  /** Let CSS size the box (the hero slot does), rather than width/height. */
  fluid = false,
  tone = 'dark',
  /** Force the two-shape reduction independently of `size`. */
  reduced = false,
  /**
   * Decorative by default: on the sign-in hero the brand name is already in the
   * DOM as screen-reader text, so announcing the mark as well would say it
   * twice. Pass `labelled` where the mark is the only brand present.
   */
  labelled = false,
  className = '',
  style = null,
}) {
  const palette = MARK_COLORS[tone] || MARK_COLORS.dark;
  const rings = ringsForSize(reduced ? 0 : size);

  return (
    <svg
      className={`prime-diamond-mark${className ? ` ${className}` : ''}`}
      data-testid="prime-diamond-mark"
      data-tone={tone}
      data-reduced={rings.length < 3 ? 'true' : 'false'}
      viewBox={VIEW}
      {...(fluid ? {} : { width: size, height: size })}
      style={style}
      focusable="false"
      {...(labelled
        ? { role: 'img', 'aria-label': PRIME_MARK_ACCESSIBLE_NAME }
        : { 'aria-hidden': 'true' })}
    >
      {rings.map((ring) => {
        // In the reduced form the inner ring is the outermost shape left, so it
        // carries the full-strength outline colour rather than the 35% one.
        const colour = ring.filled ? palette.core : palette[ring.id] || palette.inner;
        return (
          <path
            key={ring.id}
            data-ring={ring.id}
            d={diamondPath(ring.r)}
            fill={ring.filled ? colour : 'none'}
            stroke={ring.filled ? 'none' : colour}
            strokeWidth={ring.filled ? undefined : ring.stroke}
            // Mitred joins keep the four points sharp; the default round join
            // visibly clips them at this stroke-to-size ratio.
            strokeLinejoin="miter"
            strokeMiterlimit={8}
          />
        );
      })}
    </svg>
  );
}

export default PrimeDiamondMark;
